import { useState, useEffect, useRef, useCallback } from 'react';

const LAST_VISIT_KEY = 'wwi_last_visit';
const DRIFT_TIMEOUT = 150000; // 2.5 minutes

/**
 * useInactivityCues - Manages pause state and thread drift detection
 * Shows "Welcome back" after 10+ minutes away
 * Shows subtle glow on "Find my thread" after 2.5min inactivity
 */
export const useInactivityCues = (isDemo = false) => {
  // Pause State — gentle "Welcome back" when returning after 10+ minutes
  const [showPauseState, setShowPauseState] = useState(false);
  
  // Thread Drift — subtle pulse on "Find my thread" after inactivity
  const [threadDriftGlow, setThreadDriftGlow] = useState(false);
  const driftTimerRef = useRef(null);

  // Check for pause state on mount
  useEffect(() => {
    if (isDemo) return;
    
    const lastVisit = localStorage.getItem(LAST_VISIT_KEY);
    const now = Date.now();
    localStorage.setItem(LAST_VISIT_KEY, now.toString());
    
    if (lastVisit) {
      const elapsed = now - parseInt(lastVisit, 10);
      // Show pause state if away for more than 10 minutes
      if (elapsed > 10 * 60 * 1000) {
        setShowPauseState(true);
        setTimeout(() => setShowPauseState(false), 1500);
      }
    }
  }, [isDemo]);

  // Reset drift timer on user activity
  const resetDriftTimer = useCallback(() => {
    setThreadDriftGlow(false);
    if (driftTimerRef.current) clearTimeout(driftTimerRef.current);
    
    driftTimerRef.current = setTimeout(() => {
      setThreadDriftGlow(true);
      // Auto-clear after one gentle pulse cycle
      setTimeout(() => setThreadDriftGlow(false), 1500);
    }, DRIFT_TIMEOUT);
  }, []);

  // Set up activity listeners for drift detection
  useEffect(() => {
    if (isDemo) return;
    
    resetDriftTimer();
    const events = ['click', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetDriftTimer, { passive: true }));
    
    return () => {
      events.forEach(e => window.removeEventListener(e, resetDriftTimer));
      if (driftTimerRef.current) clearTimeout(driftTimerRef.current);
    };
  }, [isDemo, resetDriftTimer]);

  // Update last visit timestamp when leaving
  useEffect(() => {
    if (isDemo) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        localStorage.setItem(LAST_VISIT_KEY, Date.now().toString());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isDemo]);

  return {
    showPauseState,
    threadDriftGlow,
    resetDriftTimer,
  };
};

export default useInactivityCues;
